import Image from 'next/image';
import Link from 'next/link';
import type { DropProduct } from '@/lib/home-v2/data';
import { fmt, formatINR, type Labels } from './ui';
import { TileActions } from './tile-actions';

export function ProductTile({ product, badge, labels }: { product: DropProduct; badge?: string; labels: Labels }) {
  // Same discount as the category page card: MRP vs the price shown (package price incl. GST)
  const off = product.price !== null && product.mrp !== null && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;
  const name = product.spec || product.code;
  return (
    // The card is always a light surface, so it sets its own text colour instead of inheriting a dark
    // section's white text (sections can switch to a dark theme from the admin config).
    <article className="flex h-full flex-col overflow-hidden rounded-[14px] border border-hv-line bg-white text-hv-ink transition-colors hover:border-hv-ink/30">
      <Link href={product.href} className="relative block h-[200px] flex-shrink-0 overflow-hidden bg-white md:h-[230px]">
        {product.image && (
          <Image src={product.image} alt={[product.code, product.spec].filter(Boolean).join(' ')} fill sizes="(min-width: 768px) 300px, 50vw" className="object-contain" />
        )}
        {badge && <span className="absolute left-3 top-3 rounded-md bg-hv-orange px-2 py-1 font-hv-mono text-[11px] text-white">{badge}</span>}
      </Link>
      <div className="flex flex-1 flex-col gap-2.5 p-3 md:p-4" style={{ background: '#F6F4F0' }}>
      <div className="truncate font-hv-mono text-xs uppercase text-hv-soft">{product.code}</div>
      <Link href={product.href} title={name} className="-mt-1 truncate font-medium hover:text-hv-orange">{name}</Link>
      {product.price !== null ? (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-lg font-semibold">
              {formatINR(product.price)}{' '}
              {product.unit && <span className="text-[13px] font-normal text-hv-soft">{fmt(labels.perUnit, { unit: product.unit })}</span>}
            </span>
            {product.granularPrice !== null && product.granularUnit && (
              <span className="whitespace-nowrap text-[13px] text-hv-soft">
                {formatINR(product.granularPrice)} {fmt(labels.perUnit, { unit: product.granularUnit })}
              </span>
            )}
          </div>
          {off > 0 && (
            <div className="flex items-baseline gap-2 text-[13px]">
              <span className="text-hv-soft line-through">{formatINR(product.mrp as number)}</span>
              <span className="font-semibold text-green-700">{fmt(labels.discount, { percent: off })}</span>
            </div>
          )}
        </div>
      ) : <span className="text-sm text-hv-soft">{labels.priceOnRequest}</span>}
      <div className="mt-auto pt-1"><TileActions productId={product.productId} variantId={product.id} labels={labels} /></div>
      </div>
    </article>
  );
}
